/**
 * @file LoginLocationPill.tsx
 * @brief Soft filled pill: circular country flag + location label.
 *
 * @details
 * Replaces emoji flags in Users / Employees tables and login-history
 * dialogs. Visual twin of `UserNativeLanguageCell` (flagcdn circle +
 * muted text on muted fill — no border).
 *
 * @dependencies
 * - components/ui/CountryFlag
 * - shared/utils/loginLocation
 *
 * @author DES
 * @date 13-07-2026
 */

import { CountryFlag } from "@/components/ui/CountryFlag";
import { formatLoginLocation } from "@/shared/utils/loginLocation";

export function LoginLocationPill({
  country,
  region,
  city,
  empty = "—",
}: {
  country?: string | null;
  region?: string | null;
  city?: string | null;
  /** Shown when no geo bits are present. */
  empty?: string;
}) {
  const loc = formatLoginLocation({ country, region, city });
  if (!loc) {
    return <span className="text-xs text-muted-foreground">{empty}</span>;
  }

  const code = (country || "").trim();
  const showFlag = code.length === 2;

  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-muted/60 py-0.5 pl-0.5 pr-2.5 text-xs text-muted-foreground">
      {showFlag ? <CountryFlag code={code} size="sm" /> : null}
      <span className="truncate">{loc}</span>
    </span>
  );
}
